"use client";

import { Web3TxRoutes } from "@/hooks/tx-flow/useDepositCalculate";
import type { SupTransactionType, SupWeb3TxSingleVault } from "@/types";
import { formatBigNumberish, getVaultDecimals, roundBigNumberValueString } from "@/utils";
import React from "react";

import useChainInfo from "@/hooks/useChainInfo";
import { FixedNumber } from "@ethersproject/bignumber";
import { SupAttributeBadge, SupHeading, SupText } from "@repo/core-ui";
import { SupGas, SupCheckCircle, SupStopwatch } from "@repo/core-ui";
import SupLoading from "@/components/Ui/Loading";
import config from "@/../config/dapp.config";

interface Web3DepositRouteProps {
  singleVault: SupWeb3TxSingleVault;
  calculatedRoute: Web3TxRoutes;
  type: SupTransactionType;
}

export default function Web3CommonRouteDirect({ singleVault, calculatedRoute, type }: Web3DepositRouteProps) {
  const { data: chainData, isLoading } = useChainInfo(calculatedRoute.in.from_chain_id);

  const routeSuperpositions = type === "deposit" ? calculatedRoute.liq?.routes[0].super_position ?? undefined : undefined;
  const gasEstimate = calculatedRoute.in.gas_estimate;

  const WrappedLoading = (
    <div className="wrappedLoader h-4 w-4">
      <SupLoading />
    </div>
  );

  return (
    <div className="w-full border-[.5px] border-neutral-700 bg-neutral p-4">
      <div className="mb-6 flex w-full items-start justify-start gap-1">
        <SupAttributeBadge label={`Direct ${type === "withdraw" ? "WITHDRAWAL" : "DEPOSIT"}`} color="warning" />
        <SupCheckCircle className="ml-auto h-[18px] w-[18px] fill-neutral-800" />
      </div>

      {/*Receiving amount*/}
      <div className="flex flex-col items-center justify-start self-stretch">
        <div className="flex flex-col items-start justify-start self-stretch">
          {routeSuperpositions ? (
            <div className="flex flex-col items-start justify-start self-stretch">
              <div className="mb-1 inline-flex h-5 items-center justify-between self-stretch">
                <SupHeading variant="h5">{formatBigNumberish(routeSuperpositions.amount, getVaultDecimals(singleVault))}</SupHeading>
                <SupText variant="subtitle2">{routeSuperpositions.symbol}</SupText>
              </div>
              <SupText variant="subtitle2" className="mt-1 h-5">
                ${roundBigNumberValueString(routeSuperpositions.usd_value, config.decimalsRoundingLimit)}
              </SupText>
            </div>
          ) : (
            <SupText variant="subtitle2" className="mt-1 h-5">
              ${singleVault.amountInFiat && FixedNumber.from(`${singleVault.amountInFiat}`).round(config.decimalsRoundingLimit).toUnsafeFloat()}
            </SupText>
          )}
        </div>

        {/*Time estimation*/}
        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <div className="flex items-center justify-start gap-1">
            <SupStopwatch />
            <SupText variant="body2">Bridge time</SupText>
          </div>
          <SupText variant="body2" family="mono" weight="bold" className={gasEstimate ? "text-success-600" : "text-warning-600"}>
            {gasEstimate ? `${gasEstimate.estimated_seconds}s` : "no estimation"}
          </SupText>
        </div>

        {/*Gas info*/}
        <div className="inline-flex h-5 items-center justify-between self-stretch">
          <div className="flex items-center justify-start gap-1">
            <SupGas />
            <SupText variant="body2">Total gas cost</SupText>
          </div>
          {isLoading ? (
            WrappedLoading
          ) : (
            <SupText variant="body2" family="mono" weight="bold" className={gasEstimate ? "text-success-600" : "text-warning-600"}>
              {gasEstimate && chainData ? `$${gasEstimate.gas_price_usd}` : "no estimation"}
            </SupText>
          )}
        </div>
      </div>
    </div>
  );
}
